import { generateClient } from 'aws-amplify/api'

/**
 * Singleton GraphQL client instance for all AppSync operations.
 * All repositories should import this client rather than calling generateClient() inline.
 */
const client = generateClient()

export { client }

/** What a GraphQL subscription delivers on each event. */
export type SubscriptionEvent = { data?: Record<string, unknown> | null }

/**
 * Start an AppSync subscription with a typed observer.
 *
 * client.graphql types a subscription only for operations generated by
 * Amplify codegen, so the hand-written subscription strings used here get
 * the Promise/Observable union. This is the one place that narrows it.
 */
export function subscribeGraphQl(
    query: string,
    variables: Record<string, unknown>,
    observer: {
        next: (event: SubscriptionEvent) => void
        error: (error: unknown) => void
    }
): { unsubscribe: () => void } {
    const observable = client.graphql({ query, variables }) as unknown as {
        subscribe: (o: typeof observer) => { unsubscribe: () => void }
    }
    return observable.subscribe(observer)
}

/** The parts of an AppSync response (or thrown error) these helpers read. */
type GraphQlError = { message: string }
type GraphQlResponseShape = {
    data?: Record<string, unknown> | null
    errors?: GraphQlError[]
}

// Amplify may throw the full GraphQL response object with an errors array
function rethrowGraphQlError(error: unknown): never {
    const errors = (error as GraphQlResponseShape | null | undefined)?.errors
    if (errors && errors.length > 0) {
        throw new Error(errors[0].message)
    }
    throw error instanceof Error ? error : new Error(JSON.stringify(error))
}

async function runGraphQl(
    query: string,
    variables: Record<string, unknown>
): Promise<GraphQlResponseShape | undefined> {
    try {
        return (await client.graphql({ query, variables })) as
            | GraphQlResponseShape
            | undefined
    } catch (error: unknown) {
        return rethrowGraphQlError(error)
    }
}

/**
 * Fetch the _version field for any model by ID.
 * Used before update/delete mutations for AppSync OPTIMISTIC_CONCURRENCY conflict resolution.
 *
 * @param modelName - The DTO model name (e.g. 'ConsultDto', 'AudioDto')
 * @param id - The record ID
 * @returns The current _version number
 * @throws Error if the record is not found
 */
export async function fetchVersion(
    modelName: string,
    id: string
): Promise<number> {
    const query = `query Get${modelName}($id: ID!) {
    get${modelName}(id: $id) { id _version }
  }`
    const response = await runGraphQl(query, { id })
    const data = response?.data?.[`get${modelName}`] as
        | { _version: number }
        | null
        | undefined
    if (!data) throw new Error(`${modelName} with id ${id} not found`)
    return data._version
}

/**
 * Execute a GraphQL mutation and extract the result by operation name.
 * Throws on GraphQL errors.
 *
 * @param query - The GraphQL mutation string
 * @param variables - The mutation variables
 * @param operationName - The key to extract from `response.data`
 * @returns The extracted mutation result
 */
export async function executeMutation<T>(
    query: string,
    variables: Record<string, unknown>,
    operationName: string
): Promise<T> {
    const response = await runGraphQl(query, variables)
    const data = response?.data?.[operationName]
    const errors = response?.errors
    if (errors && errors.length > 0) throw new Error(errors[0].message)
    return data as T
}

/**
 * Execute a GraphQL query and extract the result by operation name.
 * Throws on GraphQL errors.
 *
 * @param query - The GraphQL query string
 * @param variables - The query variables
 * @param operationName - The key to extract from `response.data`
 * @returns The extracted query result
 */
export async function executeQuery<T>(
    query: string,
    variables: Record<string, unknown>,
    operationName: string
): Promise<T> {
    const response = await runGraphQl(query, variables)
    const data = response?.data?.[operationName]
    const errors = response?.errors
    if (errors && errors.length > 0) throw new Error(errors[0].message)
    return data as T
}
